import { MapBounds } from '../types';

/**
 * Geoceller for markørspørringer.
 *
 * Bakgrunn: markørspørringen kunne bare range-filtrere på breddegrad, fordi
 * Firestore ikke støtter range på to felt samtidig. Lengdegrad ble derfor filtrert
 * i minnet ETTER lesingen, og Firestore tar full pris per dokument som leveres.
 * Målt på prod 12.08.2026: 3000 dokumenter lest, 144 innenfor utsnittet — 95,2 %
 * kastet. Samtidig rakk hvert bånd bare over 0,035° av 0,45°, så kartet viste
 * tynne striper i stedet for hele utsnittet.
 *
 * Med et diskret celle-felt blir lengdegrad en likhetsspørring, som Firestore kan
 * kombinere med `in`. Da leses bare dokumenter som faktisk ligger i utsnittet.
 *
 * To oppløsninger fordi `in` tåler maks 30 verdier: 0,5°-ruter dekker zoom 10+,
 * og 2°-ruter dekker zoom 8–9 (`MIN_ZOOM_FOR_SPOT_FETCH` er 8). Oppløsning velges
 * på FAKTISK celleantall, ikke på zoom — det tilpasser seg både bredde/høyde-
 * forhold og at lengdegrader krymper mot polene.
 */

/** Maks antall verdier Firestore tillater i en `in`-spørring. */
export const FIRESTORE_IN_LIMIT = 30;

export const GEOCELL_FINE_SIZE = 0.5;
export const GEOCELL_COARSE_SIZE = 2;

export interface GeocellResolution {
  /** Dokumentfeltet som holder celle-IDen på denne oppløsningen. */
  field: 'geocell' | 'geocell2';
  /** Rutestørrelse i grader. */
  size: number;
}

export const GEOCELL_FINE: GeocellResolution = { field: 'geocell', size: GEOCELL_FINE_SIZE };
export const GEOCELL_COARSE: GeocellResolution = { field: 'geocell2', size: GEOCELL_COARSE_SIZE };

/**
 * Celle-ID for ett punkt. `Math.floor` gir konsistent oppførsel også for negative
 * koordinater (floor(-0.2) = -1), så sør for ekvator og vest for Greenwich
 * havner i egne celler i stedet for å kollapse mot 0.
 */
export const geocellId = (lat: number, lon: number, size: number): string =>
  `${Math.floor(lat / size)}_${Math.floor(lon / size)}`;

/** Begge celle-feltene for ett punkt — det som skrives på dokumentet. */
export const geocellFields = (lat: number, lon: number): { geocell: string; geocell2: string } => ({
  geocell: geocellId(lat, lon, GEOCELL_FINE_SIZE),
  geocell2: geocellId(lat, lon, GEOCELL_COARSE_SIZE),
});

/** Alle celler et utsnitt berører på gitt oppløsning. */
export const geocellsForBounds = (bounds: MapBounds, size: number): string[] => {
  const latFrom = Math.floor(bounds.latMin / size);
  const latTo = Math.floor(bounds.latMax / size);
  const lonFrom = Math.floor(bounds.lonMin / size);
  const lonTo = Math.floor(bounds.lonMax / size);
  const cells: string[] = [];
  for (let la = latFrom; la <= latTo; la++) {
    for (let lo = lonFrom; lo <= lonTo; lo++) {
      cells.push(`${la}_${lo}`);
    }
  }
  return cells;
};

export interface GeocellQuery extends GeocellResolution {
  /** Cellene utsnittet berører, delt i grupper som hver passer i én `in`-spørring. */
  chunks: string[][];
  /** Totalt antall celler før oppdeling — nyttig for logging/telemetri. */
  cellCount: number;
}

/**
 * Velg oppløsning og del cellene i `in`-vennlige grupper.
 *
 * Finoppløsning brukes så lenge den passer i én spørring; ellers grovoppløsning.
 * Skulle selv grovoppløsningen sprenge taket (ekstremt bredt utsnitt), deles den
 * i flere grupper i stedet for å kutte celler — å kutte ville gjort markører
 * usynlige uten at noe feilet synlig.
 */
export const selectGeocellQuery = (bounds: MapBounds): GeocellQuery => {
  const fine = geocellsForBounds(bounds, GEOCELL_FINE.size);
  const chosen = fine.length <= FIRESTORE_IN_LIMIT
    ? { res: GEOCELL_FINE, cells: fine }
    : { res: GEOCELL_COARSE, cells: geocellsForBounds(bounds, GEOCELL_COARSE.size) };

  const chunks: string[][] = [];
  for (let i = 0; i < chosen.cells.length; i += FIRESTORE_IN_LIMIT) {
    chunks.push(chosen.cells.slice(i, i + FIRESTORE_IN_LIMIT));
  }
  return { ...chosen.res, chunks, cellCount: chosen.cells.length };
};
